import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { PageHero, Prose } from "@/components/site/Page";
import { PeopleGrid } from "@/components/site/PeopleGrid";
import { SmartImage } from "@/components/site/SmartImage";
import { SportProfiles, type SportCard } from "@/components/site/SportProfiles";
import { Timeline } from "@/components/site/Timeline";
import { VideoEmbed } from "@/components/site/VideoEmbed";
import { SchoolValues } from "@/components/site/SchoolValues";
import { useContent } from "@/lib/site-content";
import { pathFor, type Locale } from "@/lib/i18n";
import {
  aboutCommunityImage,
  aboutCourtyardImage,
  profileJuntaImage,
  sportsFootballImage,
  sportsPadelImage,
  sportsTennisImage,
} from "@/lib/responsive-images";
import maria from "@/assets/staff/portraits/maria.webp";



type Copy = {
  approvalLink: string;
  sportsLink: string;
  communityLink: string;
  staffLink: string;
  ctaVisit: string;
  ctaApply: string;
  ctaContact: string;
  campusAlt: string;
  schoolyardAlt: string;
  videoTitle: string;
  videoPlay: string;
  juntaAlt: string;
};

const COPY: Record<Locale, Copy> = {
  sv: {
    approvalLink: "Läs mer om våra godkännanden och utbildningen",
    sportsLink: "Läs mer",
    communityLink: "Läs mer om gemenskapen →",
    staffLink: "Möt personalen på skolan →",
    ctaVisit: "Boka ett besök",
    ctaApply: "Ansök till skolan",
    ctaContact: "Kontakta oss",
    campusAlt: "Skuggig innergård med olivträd, planteringar och bänk",
    schoolyardAlt: "Långbord under palmer dukat för gemensam samling",
    videoTitle: "Film om Swedish International School",
    videoPlay: "Spela filmen om skolan",
    juntaAlt: "Junta de Andalucía – godkänd svensk utbildning i Spanien",
  },
  en: {
    approvalLink: "Read more about our approvals and the education",
    sportsLink: "Read more",
    communityLink: "Read more about the community →",
    staffLink: "Meet the staff at the school →",
    ctaVisit: "Book a visit",
    ctaApply: "Apply to the school",
    ctaContact: "Contact us",
    campusAlt: "Shaded courtyard with an olive tree, planters and a bench",
    schoolyardAlt: "A long table under palm trees set for a shared gathering",
    videoTitle: "Film about Swedish International School",
    videoPlay: "Play the film about the school",
    juntaAlt: "Junta de Andalucía – approved Swedish education in Spain",
  },
  es: {
    approvalLink: "Más sobre nuestras autorizaciones y la enseñanza",
    sportsLink: "Leer más",
    communityLink: "Más sobre la comunidad →",
    staffLink: "Conoce al equipo del colegio →",
    ctaVisit: "Reservar una visita",
    ctaApply: "Solicitar plaza",
    ctaContact: "Contacto",
    campusAlt: "Patio sombreado con olivo, jardineras y un banco",
    schoolyardAlt: "Una mesa larga bajo las palmeras preparada para una reunión",
    videoTitle: "Vídeo sobre Swedish International School",
    videoPlay: "Ver el vídeo del colegio",
    juntaAlt: "Junta de Andalucía – enseñanza sueca autorizada en España",
  },
};

const SCHOOL_VIDEO_ID = "1043905042";

function sportCards(locale: Locale): SportCard[] {
  return [
    {
      to: pathFor("/fotbollsakademi", locale),
      image: sportsFootballImage,
      alt:
        locale === "sv"
          ? "Ungdomar tränar fotboll på gräsplan i medelhavsmiljö"
          : locale === "es"
            ? "Jóvenes entrenando fútbol en un campo de hierba"
            : "Teenagers training football on a grass pitch",
    },
    {
      to: pathFor("/padel-tennis", locale),
      image: sportsPadelImage,
      alt:
        locale === "sv"
          ? "Ungdomar spelar padel på bana med glasväggar"
          : locale === "es"
            ? "Jóvenes jugando al pádel en una pista acristalada"
            : "Teenagers playing padel on a glass-walled court",
    },
    {
      to: pathFor("/padel-tennis", locale),
      image: sportsTennisImage,
      alt:
        locale === "sv"
          ? "Tennisträning på grusbana med bergen i bakgrunden"
          : locale === "es"
            ? "Entrenamiento de tenis en pista de tierra batida"
            : "Tennis training on a clay court with mountains behind",
    },
  ];
}


/**
 * Gemensam sidkropp för Om skolan på svenska, engelska och spanska.
 * Berättelsens ordning: intro → idé → tidslinje → grundare och ledning →
 * godkännande → idrott → miljö → gemenskap → personal → besök/ansökan.
 * Tidslinjen visas medvetet utan foton tills äkta bilder från respektive
 * år finns (skolstart 2023, invigning 2024, MFF/landslag 2025, padel 2026).
 */
export function AboutSchoolPage() {
  const { locale, text, list, paragraphs } = useContent("om-skolan");
  const copy = COPY[locale];

  return (
    <>
      <PageHero
        eyebrow={text("hero.eyebrow")}
        title={text("hero.title")}
        intro={text("hero.intro")}
      />

      {/* 1. En svensk skola med internationella möjligheter */}
      <Prose>
        <h2>{text("intro.title")}</h2>
        {paragraphs("intro.text").map((p, i) => (
          <p key={i}>{p}</p>
        ))}

        <SmartImage
          image={aboutCourtyardImage}
          alt={copy.campusAlt}
          width={1024}
          height={683}
          sizes="(min-width: 768px) 48rem, 100vw"
          className="w-full rounded-xl shadow-soft"
        />

        {/* 2. Från idé till skola */}
        <h2>{text("story.title")}</h2>
        {paragraphs("story.text").map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </Prose>

      <section className="mx-auto max-w-3xl px-5 pb-4">
        <VideoEmbed
          videoId={SCHOOL_VIDEO_ID}
          title={copy.videoTitle}
          playLabel={copy.videoPlay}
        />
      </section>


      <SchoolValues />


      {/* 3. Vår historia */}
      <section className="border-y border-border bg-sand">
        <div className="mx-auto max-w-3xl px-5 py-14 sm:py-16">
          <h2 className="text-2xl font-semibold sm:text-3xl">{text("timeline.title")}</h2>
          <div className="mt-8">
            <Timeline items={list("timeline")} />
          </div>
        </div>
      </section>

      {/* 4. Skolledning – de som möter eleverna varje dag */}
      <section className="mx-auto max-w-5xl px-5 py-14 sm:py-16">
        <h2 className="text-2xl font-semibold sm:text-3xl">{text("leadership.title")}</h2>
        <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
          {text("leadership.intro")}
        </p>
        <div className="mt-8">
          <PeopleGrid
            people={list("leadership")}
            images={[{ image: maria, alt: "Maria Wikström", personKey: "maria" }]}
          />
        </div>
      </section>

      {/* 5. Godkänd svensk utbildning i Spanien */}
      <section className="border-y border-border bg-sand">
        <div className="mx-auto max-w-3xl px-5 py-14 sm:py-16">
          <h2 className="text-2xl font-semibold sm:text-3xl">{text("approval.title")}</h2>
          {paragraphs("approval.text").map((p, i) => (
            <p key={i} className="mt-3 leading-relaxed text-muted-foreground">
              {p}
            </p>
          ))}
          <div className="mt-8">
            <SmartImage
              image={profileJuntaImage}
              alt={copy.juntaAlt}
              width={1024}
              height={683}
              sizes="(min-width: 768px) 48rem, 100vw"
              className="w-full rounded-xl border border-border shadow-soft"
            />
          </div>
          <div className="mt-8">
            <Link
              to={pathFor("/junta-de-andalucia-och-utbildning", locale) as never}
              className="group inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-center text-sm font-semibold text-primary-foreground shadow-soft transition-all hover:shadow-md hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:w-auto"
            >
              <span>{text("approval.link") || copy.approvalLink}</span>
              <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" aria-hidden />
            </Link>
          </div>

        </div>
      </section>

      {/* 6. Idrott som en del av skoldagen */}
      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
        <h2 className="text-2xl font-semibold sm:text-3xl">{text("sports.title")}</h2>
        <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
          {text("sports.intro")}
        </p>
        <div className="mt-8">
          <SportProfiles
            items={list("sports")}
            cards={sportCards(locale)}
            linkLabel={copy.sportsLink}
          />
        </div>
      </section>

      <Prose>
        {/* 7. Villa Padierna och miljön runt skolan */}
        <h2>{text("villa.title")}</h2>
        {paragraphs("villa.text").map((p, i) => (
          <p key={i}>{p}</p>
        ))}

        <SmartImage
          image={aboutCommunityImage}
          alt={copy.schoolyardAlt}
          width={1024}
          height={683}
          sizes="(min-width: 768px) 48rem, 100vw"
          className="w-full rounded-xl shadow-soft"
        />

        {/* 8. Gemenskap och traditioner */}
        <h2>{text("community.title")}</h2>
        {paragraphs("community.text").map((p, i) => (
          <p key={i}>{p}</p>
        ))}
        <p>
          <Link
            to={pathFor("/gemenskap", locale) as never}
            className="font-semibold text-primary link-quiet"
          >
            {copy.communityLink}
          </Link>
        </p>

        {/* 9. Våra lärare och medarbetare */}
        <h2>{text("staff.title")}</h2>
        <p>{text("staff.text")}</p>
        <p>
          <Link
            to={pathFor("/personal", locale) as never}
            className="font-semibold text-primary link-quiet"
          >
            {copy.staffLink}
          </Link>
        </p>
      </Prose>

      {/* 9b. Grundare och styrelse – trust längre ner */}
      <section className="mx-auto max-w-5xl px-5 py-14 sm:py-16">
        <h2 className="text-2xl font-semibold sm:text-3xl">{text("founders.title")}</h2>
        <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
          {text("founders.intro")}
        </p>
        <p className="mt-4">
          <Link
            to={pathFor("/personal", locale) as never}
            className="font-semibold text-primary link-quiet"
          >
            {copy.staffLink}
          </Link>
        </p>
      </section>

      <div className="pt-14">
        <FaqBlock items={faqFor("om-skolan", locale)} locale={locale} />
      </div>

      {/* 10. Avslutning och call to action */}
      <section className="mx-auto max-w-6xl px-5">
        <div className="rounded-xl bg-primary px-6 py-10 text-primary-foreground sm:px-12 sm:py-12">
          <h2 className="max-w-xl text-2xl font-semibold sm:text-3xl">{text("cta.title")}</h2>
          {paragraphs("cta.text").map((p, i) => (
            <p key={i} className="mt-3 max-w-xl leading-relaxed text-primary-foreground">
              {p}
            </p>
          ))}
          <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5">
            <Link
              to={pathFor("/boka-besok", locale) as never}
              className="flex min-h-11 items-center justify-center rounded-md bg-background px-5 text-sm font-medium text-primary transition-opacity hover:opacity-90"
            >
              {copy.ctaVisit}
            </Link>
            <Link
              to={pathFor("/ansokan", locale) as never}
              className="flex min-h-11 items-center justify-center rounded-md border border-primary-foreground/40 px-5 text-sm font-medium transition-colors hover:bg-primary-foreground/10"
            >
              {copy.ctaApply}
            </Link>
            <Link
              to={pathFor("/kontakt", locale) as never}
              className="flex min-h-11 items-center justify-center rounded-md border border-primary-foreground/40 px-5 text-sm font-medium transition-colors hover:bg-primary-foreground/10"
            >
              {copy.ctaContact}
            </Link>
          </div>
        </div>
      </section>
      <div className="h-20" />
    </>
  );
}
